// utils/buildInitialLegacyState.ts
import { STEP, NON_CC, BILLING_ADDRESS, SHIPPING_CODES } from '@/constants';
import { REGION, COOKIE_BANNER_REGIONS, GDPR_REGIONS, URLS } from '@/regions';
import { parseIsoCountryCode } from '@/utils/i18nFields';

export const defaultShipMethod = {
  code: SHIPPING_CODES.STANDARD,
  displayPrice: '$9.95',
  title: 'Standard',
};

export function buildInitialLegacyState(checkout: CheckoutContext): InitialState {
  // fallback values
  const {
    account,
    billingCountries,
    cart,
    kit,
    expressStatesBlacklist,
    hasSubscribedProfile,
    hideUkPromoPricing,
    showUkShippingUpdate,
    showIntShippingUpdate,
    isChipUpgrade,
    isPrepaidSubscription,
    hasDiscountedPrepaidSubscription,
    hasTotalHealth,
    hasTotalHealthUpgrade,
    isPremiumAncestry = false,
    isEmptyCart,
    hasPendingTotalHealth,
    isApiUp,
    isSubsEnabled,
    premiumNamingEnabled = false,
    payment,
    planId,
    eligiblePlans = [],
    selectedPlanId = '',
    profile,
    profiles,
    isMultiProfileAccount,
    shipMethod,
    shippingAddress,
    shippingCountries,
    shippingMethods,
    states,
    subscriptionInfo,
    subBundleInfo,
    billingInfo,
    openSubsTab,
    hasSurveyResults,
    pubUrl,
    successUrl,
    isMobileWebView,
    heapId,
    region,
    membership,
    labs = {},
    clinic = {},
    orderCreationDate,
    preserveDefaultPayment,
    showHistoricalMatches,
    enableApplepay,
    enableGooglePay,
    enablePaypal,
    enableSavedPaymentsPaypal,
    enableVenmo,
    gtmNullPayload,
    upsellSource,
    upgradeSource,
    braintreeToken,
    paypalClientId,
    googlePayMerchantId,
    utmData,
    walletMissingFields,
  } = checkout;

  const gdprAvailable = true;
  const nonCC = payment && !!NON_CC[payment.method];
  const hasIncompleteWalletAddress = !!walletMissingFields?.length;
  const hasSavedCreditCard = !!(
    account?.paymentMethods?.length && account.paymentMethods[0]?.isCreditCard
  );
  // A wallet is not the vaulted card: seeding SAVED here would send that card's billing address to
  // the gateway for a payment it has nothing to do with, and nothing downstream corrects it — the
  // auto-skip below means Billing.handleChange, the only reset, never runs (BKO-5416).
  const seedFromSavedCard = hasSavedCreditCard && !nonCC;
  const addressSourceKey = seedFromSavedCard ? 'SAVED' : 'SHIPPING';

  let customAddress = {};
  if (seedFromSavedCard && !Object.keys(labs).length) {
    const pm = account.paymentMethods[0];
    const { firstName, lastName, address, city, state, country, postalCode } = pm;
    customAddress = { firstName, lastName, address, city, state, country, postalCode };
  }

  // When the wallet returned an address that's missing required fields, suppress
  // the auto-skip-to-billing flow that nonCC normally triggers. The user needs to
  // land on the address step with the wallet's partial data prefilled so they can
  // fill in what's missing through the regular form.
  const shouldAutoSkipForWallet = nonCC && !hasIncompleteWalletAddress;

  return {
    nonCC: shouldAutoSkipForWallet,
    walletMissingFields,
    accountEmail: isChipUpgrade || hasTotalHealthUpgrade ? account.email : null,
    expressStatesBlacklist,
    billingCountries,
    profile: isChipUpgrade || hasTotalHealthUpgrade ? profile : null,
    isMultiProfileAccount,
    isChipUpgrade,
    shippingCountries,
    showGDPR: GDPR_REGIONS.includes(REGION),
    hasSavedCreditCard,
    account,
    address: {
      ...shippingAddress,
      country: shippingAddress?.country || parseIsoCountryCode(REGION),
    },
    billing: {
      addressSource: BILLING_ADDRESS[addressSourceKey],
      customAddress,
      enableSavedPayment: false,
      saveCreditCardOptIn: false,
      lastFour: '1111',
      cardType: 'Visa',
      ...payment,
    },
    cart,
    confirmation: {
      body: '',
      title: '',
    },
    kit,
    displayAuthenticationWindow: false,
    editableClass: '',
    error: {
      backTo: 'address',
      body: '',
      button: '',
      countryCode: '',
      method: '',
      step: STEP.ADDRESS,
      title: '',
    },
    hasSubscribedProfile,
    hideUkPromoPricing,
    showUkShippingUpdate,
    showIntShippingUpdate,
    isBillingPromo: false,
    isSubsEnabled,
    premiumNamingEnabled,
    isPrepaidSubscription,
    hasTotalHealth,
    hasTotalHealthUpgrade,
    isPremiumAncestry,
    isEmptyCart,
    hasPendingTotalHealth,
    hasDiscountedPrepaidSubscription,
    isApiUp,
    loading: shouldAutoSkipForWallet,
    paymentMethod: payment?.method ?? null,
    profiles,
    shipMethod: shippingMethods?.find((m) => m.code === shipMethod) || defaultShipMethod,
    shippingMethods,
    shippingStates: states ? [...states] : [],
    billingStates: states ? [...states] : [],
    showCookieBanner: COOKIE_BANNER_REGIONS.includes(REGION),
    step: shouldAutoSkipForWallet ? STEP.BILLING : STEP.ADDRESS,
    verifiedAddress: null,
    showOrderDetails: isChipUpgrade,
    modalOpen: '',
    isProcessing: false,
    pubUrl: pubUrl || URLS.HOME,
    planId,
    subscriptionInfo,
    eligiblePlans,
    selectedPlanId,
    subBundleInfo,
    billingInfo,
    openSubsTab,
    hasSurveyResults,
    successUrl,
    isMobileWebView,
    heapId,
    region,
    membership,
    labs,
    clinic,
    enablePaypal,
    enableSavedPaymentsPaypal,
    enableVenmo,
    enableApplepay: enableApplepay && window.ApplePaySession?.canMakePayments(),
    enableGooglePay,
    gtmNullPayload,
    braintreeToken,
    paypalClientId,
    googlePayMerchantId,
    areHostedFieldsPopulated: false,
    braintreeInstance: null,
    inputFields: null,
    orderCreationDate,
    preserveDefaultPayment,
    showHistoricalMatches,
    upsellSource,
    upgradeSource,
    utmData,
  };
}
